import React, { useEffect, useMemo, useState } from "react";
import api from "../context/api";
import "../styles/WinAbove.css";

const normalizeUsers = (raw) => {
  if (!Array.isArray(raw)) return [];

  return raw
    .map((item) => {
      if (!item || typeof item !== "object") return null;

      return {
        id: item._id || item.id || item.phoneNumber || Math.random().toString(36).slice(2),
        fullName: item.fullName || item.name || "N/A",
        phoneNumber: item.phoneNumber || "N/A",
      };
    })
    .filter(Boolean);
};

const WinAbove = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [bulkMessage, setBulkMessage] = useState("");
  const [sending, setSending] = useState(false);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await api.get("/users/phone-numbers");
      setUsers(normalizeUsers(response.data));
    } catch (error) {
      const msg =
        error.response?.data?.error ||
        error.response?.data?.message ||
        error.message ||
        "Failed to fetch users";
      window.alert(msg);
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const usersCount = useMemo(() => users.length, [users]);

  const handleExport = () => {
    if (!users.length) {
      window.alert("There are no users to export.");
      return;
    }

    const header = "Full Name,Phone Number";
    const rows = users.map((user) => {
      const safeName = String(user.fullName || "").replace(/"/g, '""');
      const safePhone = String(user.phoneNumber || "").replace(/"/g, '""');
      return `"${safeName}","${safePhone}"`;
    });

    const csvContent = [header, ...rows].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");

    link.href = url;
    link.setAttribute("download", `huntex-users-${Date.now()}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleSendBulkSms = async () => {
    if (!bulkMessage.trim()) {
      window.alert("Please enter a message to send.");
      return;
    }

    if (!window.confirm("Send this SMS to all users?")) {
      return;
    }

    try {
      setSending(true);
      const response = await api.post("/admin/bulk-sms", {
        message: bulkMessage.trim(),
      });

      const { sent, total, failed } = response.data || {};
      window.alert(`Sent: ${sent ?? 0} / ${total ?? 0}\nFailed: ${failed ?? 0}`);
    } catch (error) {
      const msg =
        error.response?.data?.error ||
        error.response?.data?.message ||
        error.message ||
        "Failed to send bulk SMS";
      window.alert(msg);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="winabove-page">
      <div className="winabove-card">
        <div className="winabove-header">
          <h1>Users & Bulk SMS</h1>
          <p>Total users: {usersCount}</p>
        </div>

        <div className="winabove-actions">
          <button type="button" className="winabove-btn secondary" onClick={fetchUsers} disabled={loading}>
            {loading ? "Refreshing..." : "Refresh Users"}
          </button>
          <button type="button" className="winabove-btn" onClick={handleExport} disabled={loading || !users.length}>
            Export CSV
          </button>
        </div>

        <div className="winabove-bulk">
          <label htmlFor="bulkMessage">Bulk SMS message</label>
          <textarea
            id="bulkMessage"
            value={bulkMessage}
            onChange={(event) => setBulkMessage(event.target.value)}
            placeholder="Type message to send to all users"
            rows={4}
          />
          <button
            type="button"
            className="winabove-btn"
            onClick={handleSendBulkSms}
            disabled={sending || !users.length}
          >
            {sending ? "Sending..." : "Send SMS to All Users"}
          </button>
        </div>

        <div className="winabove-list-wrap">
          {loading ? (
            <div className="winabove-empty">Loading users...</div>
          ) : users.length === 0 ? (
            <div className="winabove-empty">No users found.</div>
          ) : (
            <table className="winabove-table">
              <thead>
                <tr>
                  <th>Full Name</th>
                  <th>Phone Number</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user.id}>
                    <td>{user.fullName}</td>
                    <td>{user.phoneNumber}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default WinAbove;
